import {
  Box,
  Drawer,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React from 'react';
import Form from 'components/form';
import TextField from 'components/form/Input';
import Select from 'components/form/Select';
import Button from 'components/common/Button';
import CloseIcon from '@material-ui/icons/Close';
import DateInput from 'components/form/DateInput';
import { DateTimePicker } from '@material-ui/pickers';
import useSnack from 'hooks/useSnack';
import { useSelector } from 'react-redux';
import { GET_USERS, GET_STUDENTS } from 'graphql/todos';
import { getFullName } from 'utils';
import { useQuery } from 'react-apollo';

const { FormItem } = Form;

const useStyles = makeStyles(() => ({
  drawer: {
    width: 600,
    padding: 20,
  },
  close: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
}));

const PRIORITY = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

const CreateTodo = ({ task, form, onClose, onSave }) => {
  const classes = useStyles();
  const snack = useSnack();
  const user = useSelector((state) => state.user.user);
  const { data, loading: loadingUsers } = useQuery(GET_USERS);
  const studentsResp = useQuery(GET_STUDENTS);

  const { getFieldDecorator, validateFields, getFieldValue } = form;
  const studentId = getFieldValue('student_id') || task?.student_id;

  const users = data
    ? data.users.map((i) => ({
        label: getFullName(i),
        value: i.user_id,
        ...i,
      }))
    : [];

  const student =
    studentId &&
    studentsResp.data &&
    studentsResp.data.rows.find((i) => i.user_id === studentId);

  const filteredUsers = student
    ? users.filter(
        (u) =>
          u.value === student.admin_id ||
          u.value === student.lc_id ||
          u.value === student.hc_id ||
          u.value === student.fa_id ||
          u.value === student.user_id,
      )
    : users;

  const onSubmit = () => {
    return validateFields().then(async (values) => {
      if (task) {
        values.id = task.id;
      }
      values.assigned_by = user.id;
      if (!values.assigned_to) {
        values.assigned_to = user.id;
      }
      return onSave(values).then(
        () => {
          snack.info('Task saved.');
          onClose();
        },
        (e) => snack.error(e.message),
      );
    });
  };

  return (
    <Drawer
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
      open
      anchor="right"
      classes={{ paper: classes.drawer }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={3}
      >
        <Typography variant="h5">
          {task ? 'Edit Task' : 'Add Task'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box>
        <Form>
          <FormItem>
            {getFieldDecorator('title', {
              initialValue: task?.title,
              rules: [{ required: true }],
            })(<TextField label="Title" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('student_id', {
              initialValue: task?.student_id,
            })(
              <Select
                transformValue
                loading={studentsResp.loading}
                isMulti={false}
                options={studentsResp.data?.rows.map((i) => ({
                  label: i.name,
                  value: i.user_id,
                }))}
                label="Student"
              />,
            )}
          </FormItem>
          { (user.type === 'admin' || student) && (
            <FormItem>
              {getFieldDecorator('assigned_to', {
                initialValue: task?.assigned_to,
                rules: [{ required: true }],
              })(
                <Select
                  loading={loadingUsers}
                  transformValue
                  isMulti={false}
                  options={filteredUsers}
                  label="Assigned To"
                />,
              )}
            </FormItem>
          )}
          <FormItem>
            {getFieldDecorator('priority', {
              initialValue: task?.priority,
              rules: [{ required: true }],
            })(
              <Select
                transformValue
                isMulti={false}
                options={PRIORITY}
                label="Priority"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('due_date', {
              initialValue: task?.due_date,
              rules: [{ required: true }],
            })(
              <DateInput
                style={{ paddingBottom: '1rem' }}
                disablePast
                format="MM-DD-YYYY"
                label="Due date"
                inputVariant="outlined"
                fullWidth
                margin="dense"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('reminder', {
              initialValue: task?.reminder || null,
              rules: [{ required: true }],
            })(
              <DateInput
                time
                style={{ paddingBottom: '1rem' }}
                clearable
                disablePast
                format="MM-DD-YYYY HH:mm"
                label="Reminder"
                inputVariant="outlined"
                fullWidth
                margin="dense"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('note', {
              initialValue: task?.note,
              rules: [{ required: true }],
            })(<TextField multiline variant="outlined" label="Note" />)}
          </FormItem>
          <Box marginTop={3}>
            <Button variant="contained" color="primary" onClick={onSubmit}>
              Save
            </Button>
          </Box>
        </Form>
      </Box>
    </Drawer>
  );
};

export default Form.create()(CreateTodo);
